import React, { ReactElement } from 'react'
import { Flex, Box, Text } from 'theme-ui'
import BrowseFasterAnimation from 'images/homePage/browse-faster.svg'
import BypassGeoblocksAnimation from 'images/homePage/bypass-geoblocks.svg'
import ProfilesDevices from 'images/homePage/profiles-devices.svg'
import BrowseFasterIcon from 'images/homePage/browse-faster-icon.svg'
import BypassGeoblocksIcon from 'images/homePage/bypass-geoblocks-icon.svg'
import ParentalControlsIcon from 'images/homePage/parental-controls-icon.svg'
import ImproveProductivityIcon from 'images/homePage/improve-productivity-icon.svg'
import TeleportIcon from 'images/homePage/teleport-icon.svg'
import DevicesProfilesIcon from 'images/homePage/devices-profiles-icon.svg'
import UseCaseItem, {
  UseCaseItemSideType,
  UseCasesContentType,
} from 'components/HomePage/UseCaseItem'
import CONTROLDLogo, { CONTROLDText } from 'ui/CONTROLD'
import ImproveProductivitySvg from 'images/homePage/improve-productivity.svg'
import DefaultLocation from 'images/homePage/teleport.svg'
import ParentalControlToggles from 'images/featuresPages/parental-control-toggles.svg'

export const UseCasesContent: UseCasesContentType[] = [
  {
    icon: BrowseFasterIcon,
    image: BrowseFasterAnimation,
    title: 'Browse faster.',
    description: (
      <>
        When you browse the internet, you download more tracking and ad code than the content you
        want to see. What a waste of bandwidth. Get everything you need - and nothing you don’t -
        with {CONTROLDText}.
      </>
    ),
    shortDescription: 'Less tracking and more downloading.',
    buttonText: 'Browse faster',
    linkTo: '/browse-faster',
    leftSideType: UseCaseItemSideType.IMAGE,
    animationDuration: 4000,
  },
  {
    icon: ImproveProductivityIcon,
    image: ImproveProductivitySvg,
    title: 'Improve productivity.',
    description: (
      <>
        The Internet is full of distractions. {CONTROLDText} can enforce a productivity schedule
        that you create, blocking the stuff you don’t want to see, when you don’t want to see it.
      </>
    ),
    shortDescription: "Block the stuff you don't want to see, when you don't want to see it.",
    buttonText: 'Be more productive',
    linkTo: '/improve-productivity',
    leftSideType: UseCaseItemSideType.TEXT,
  },

  {
    icon: TeleportIcon,
    image: DefaultLocation,
    title: 'Teleport to other countries.',
    description:
      'Change your external IP address with a flip of a switch, with no VPN applications to install. You can use any number of our 100+ locations simultaneously.',
    shortDescription: 'Change your external IP address with a flip of a switch, with no VPN.',
    buttonText: 'Change countries',
    linkTo: '/teleport',
    leftSideType: UseCaseItemSideType.IMAGE,
  },
  {
    icon: ParentalControlsIcon,
    image: ParentalControlToggles,
    title: 'Keep your kids safe.',
    description: (
      <>
        You know your kids better than anybody. {CONTROLDText} gives you the tools to help them
        navigate the Internet safely. You can block adult content, drug websites, or anything else
        that you feel isn’t right for your family. You have the control.
      </>
    ),
    shortDescription:
      'Block adult content, drug websites, or anything else that you feel isn’t right for your family.',
    buttonText: 'Keep your kids safe',
    linkTo: '/parental-controls',
    leftSideType: UseCaseItemSideType.TEXT,
  },
  {
    icon: BypassGeoblocksIcon,
    image: BypassGeoblocksAnimation,
    title: 'Control services.',
    description: (
      <>
        Create rules for individual web services and popular websites with a single click. Choose to
        block services you don&apos;t like, or redirect them via a location of your choice.
        Different services can be redirected to different locations.
      </>
    ),
    shortDescription: `Take control of web services.`,
    buttonText: 'Control services',
    linkTo: '/available-services',
    leftSideType: UseCaseItemSideType.IMAGE,
  },
  {
    icon: DevicesProfilesIcon,
    image: ProfilesDevices,
    title: 'Different devices, different rules.',
    description:
      'Create unique browsing profiles for all your family devices, all under one account.',
    shortDescription: 'Create unique browsing profiles all under one account.',
    buttonText: 'Create unique profiles',
    linkTo: '/multi-device',
    leftSideType: UseCaseItemSideType.TEXT,
  },
]

const UseCases = (): ReactElement => {
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Text
        as="h2"
        sx={{
          fontSize: ['2.8rem', '4rem'],
          fontWeight: 'normal',
          mt: ['2.4rem', '8rem'],
          mb: '3.2rem',
          width: '100%',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        What can I do with <CONTROLDLogo Dsx={{ ml: '0.8rem' }} />?
      </Text>

      <Box id="use-cases" sx={{ width: '100%' }}>
        {UseCasesContent.map(item => (
          <UseCaseItem key={item.title} {...item} />
        ))}
      </Box>
    </Flex>
  )
}

export default UseCases
